<template>
    <div id="company-tab" class="company-tab">
        <customModalDialog :settings="customModalSettings"></customModalDialog>
        <div class="columns">
            <div class="company-info">
                <div :class="{'hidden' : !pageLoad}">
                    <h4>{{$ml.get('companyInformation')}}</h4>
                    <form>
                        <div class="company-input">
                            <img src="@/assets/icons/ic_case.svg">
                            <md-field
                                    :class="{'md-invalid': $v.organization.official_name.$dirty && $v.organization.official_name.$invalid}">
                                <label>{{ translate('bi_OfficialName') }}</label>
                                <md-input
                                        v-model="organization.official_name"
                                        type="text"
                                        @input="$v.organization.official_name.$touch()"
                                ></md-input>
                                <span class="md-error">{{ translate('bi_validation_OrganizationRequired') }}</span>
                            </md-field>
                        </div>

                        <div class="company-input">
                            <img src="@/assets/icons/ic_mail.svg">

                            <md-field
                                    :class="{'md-invalid': $v.organization.contact_email.$dirty && $v.organization.contact_email.$invalid}">
                                <label>{{ translate('bi_ContactEmail') }}</label>
                                <md-input
                                        v-model="organization.contact_email"
                                        type="text"
                                        @input="$v.organization.contact_email.$touch()"
                                ></md-input>
                                <span class="md-error">{{ translate('bi_validation_Email') }}</span>
                            </md-field>
                        </div>

                        <div class="company-input">
                            <img src="@/assets/icons/ic_phone.svg">
                            <md-field class="phone-custom" ref="phoneField"
                                      :class="{'md-invalid': $v.organization.phone.$dirty && $v.organization.phone.$invalid}">
                                <label>{{ translate('bi_Phone') }}</label>
                                <vue-tel-input
                                        id="tooltip-phone"
                                        v-model="organization.phone"
                                        placeholder=""
                                        :inputOptions="{showDialCode: true}"
                                        @input="onInputPhoneField"
                                        @blur="onBlurPhoneField"
                                ></vue-tel-input>
                                <span class="md-error">{{ translate('bi_validation_Phone') }}</span>

                                <b-tooltip target="tooltip-phone" variant="info">Ex: +38 050 777-77-77</b-tooltip>
                            </md-field>
                        </div>

                        <div class="company-input">
                            <img src="@/assets/icons/ic_pin.svg">
                            <md-field>
                                <label>{{ translate('bi_Address') }}</label>
                                <md-input
                                        v-model="organization.address"
                                        type="text"
                                ></md-input>
                            </md-field>
                        </div>

                        <div class="company-input">
                            <img src="@/assets/icons/ic_globe.svg">
                            <md-field
                                    :class="{'md-invalid': $v.organization.website.$dirty && $v.organization.website.$invalid}">
                                <label>{{ translate('bi_WebsiteUrl') }}</label>
                                <md-input
                                        v-model="organization.website"
                                        type="text"
                                        @input="$v.organization.website.$touch()"
                                ></md-input>
                                <span class="md-error">{{ translate('bi_validation_Website') }}</span>
                            </md-field>
                        </div>

                    </form>
                    <div class="button-group">
                        <b-button
                                block
                                class="primaryButton"
                                :class="{disabled: $v.organization.$anyError && $v.organization.$invalid}"
                                :disabled="$v.organization.$anyError && $v.organization.$invalid || saveInfo"
                                @click="saveBusinessInfo"
                        >
                            <b-spinner v-if="saveInfo" small></b-spinner>
                            <span v-else> {{translate('saveChanges')}} </span>
                        </b-button>

                        <b-button
                                v-if="canDoMultiple"
                                block
                                class="primaryButton"
                                :disabled="canDoMultipleLoader"
                                @click="setMultipleSitesModeConfirm"
                        >
                            <b-spinner v-if="canDoMultipleLoader" small></b-spinner>
                            <span v-else> {{translate('MyBusinessIsMultipleSites')}} </span>
                        </b-button>
                    </div>
                </div>
                <div v-if="!pageLoad" class="block-spinner">
                    <b-spinner style="width: 3rem; height: 3rem;" type="grow" label="Spinning"></b-spinner>
                </div>
            </div>
            <div class="company-managers">
                <h4>{{$ml.get('companyManagers')}}</h4>
                <p class="description">{{$ml.get('companyManagersDescription')}}</p>
                <inviteManagers :settings="inviteManagersSettings"></inviteManagers>
            </div>
        </div>
        <div class="columns">
            <div class="company-info">
                <div :class="{'hidden' : !pageLoad}">
                    <h4>{{$ml.get('company_ai_assist')}}</h4>
                    <div>
                        <b-form-checkbox
                            :disabled="aiAssistLoader"
                            v-model="aiAssistEnabled"
                            @input="onAIFilterClick" class="settings-cb">
                            <span>{{translate('company_ai_assist_cb')}}</span>
                        </b-form-checkbox>
                        <div class="cb-description">AirVote module will filter unsuitable images and enhance comments to help business stay on actionable insights.</div>
                    </div>
                </div>
                <div v-if="!pageLoad || aiAssistLoader" class="block-spinner">
                    <b-spinner style="width: 3rem; height: 3rem;" type="grow" label="Spinning"></b-spinner>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue'
    import {MdField} from 'vue-material/dist/components'
    import '@/assets/css/MdField.css'
    import errorMixin from '@/mixins/errorMixin';
    import {mapActions, mapState} from 'vuex';
    import {validationMixin} from 'vuelidate'
    import {isPhoneValid, phoneValidator, urlValidator} from '@/helpers/customValidators'
    import {VueTelInput} from 'vue-tel-input'

    import customModalDialog from '@/components/widgets/customModalDialog';
    import inviteManagers from '@/components/widgets/inviteManagers';
    import logAnalyticsEvent, {logError} from '@/helpers/analytics'

    const {required, email} = require('vuelidate/lib/validators')

    Vue.use(MdField)

    export default {
        mixins: [errorMixin, validationMixin],

        components: {
            VueTelInput,
            customModalDialog,
            inviteManagers
        },

        validations: {
            organization: {
                official_name: {
                    required
                },
                contact_email: {
                    email
                },
                phone: {
                    phone: phoneValidator
                },
                website: {
                    url: urlValidator
                }
            }
        },

        data() {
            return {
                customModalSettings: {},
                inviteManagersSettings: {},
                //
                currentPhoneCountry: null,
                pageLoad: false,
                organization: {
                    official_name: '',
                    contact_email: '',
                    phone: '',
                    address: '',
                    website: '',
                },
                aiAssistEnabled: true,
                aiAssistLoader: false,
                saveInfo: false,
                canDoMultiple: false,
                canDoMultipleLoader: false,
            }
        },

        beforeMount() {

            try {
                this.organization = {
                    official_name: this.userInfo.organization ? this.userInfo.organization.official_name : this.userInfo.organizations[0].official_name,
                    contact_email: this.userInfo.organization ? this.userInfo.organization.contact_email : this.userInfo.organizations[0].contact_email,
                    phone: this.userInfo.organization ? this.userInfo.organization.phone : this.userInfo.organizations[0].phone,
                    address: this.userInfo.organization ? this.userInfo.organization.address : this.userInfo.organizations[0].address,
                    website: this.userInfo.organization ? this.userInfo.organization.website : this.userInfo.organizations[0].website,
                }
                this.inviteManagersSettings = {
                    guid: this.userInfo.organization ? this.userInfo.organization.guid : this.userInfo.organizations[0].guid,
                    organization: true
                }
                this.aiAssistEnabled = this.userInfo.organization ? this.userInfo.organization.ai_assist_enabled : this.userInfo.organizations[0].ai_assist_enabled
            } finally {
                this.pageLoad = true;
            }
            if (!this.isMultipleSites) {
                this.canDoMultiple = true
            }

        },

        computed: {

            ...mapState({
                userInfo: state => state.auth.userInfo,
                isMultipleSites: state => state.auth.isMultipleSites
            }),

        },

        watch: {
            'organization.phone'(newValue) {
                this.$nextTick(() => {
                    this.$refs.phoneField.fieldClasses['md-focused'] = newValue.length > 0
                    if (newValue.length > 0) {
                        this.$refs.phoneField.$el.classList.add('md-has-value')
                    } else {
                        this.$refs.phoneField.$el.classList.remove('md-has-value')
                    }
                })
            },

        },

        methods: {
            ...mapActions({
                fetchUserInfo: 'auth/fetchUserInfo',
                saveBusinessInfoAction: 'owner/saveBusinessInfo',
                setMultipleSitesModeAction: 'organization/setMultipleSitesMode',
                setAIAssistMode: 'organization/setAIAssistMode',
            }),

            // Business Info
            onBlurPhoneField() {
                let validate = this.organization.phone.replace(/[' ']/g, '').replace(/-/g, '')
                if (isPhoneValid(validate)) {
                    this.$refs.phoneField.$el.classList.remove('md-invalid')
                } else {
                    this.$refs.phoneField.$el.classList.add('md-invalid')
                }
            },

            onInputPhoneField(value, obj) {
                this.currentPhoneCountry = `+${obj.country.dialCode}`
                this.$v.organization.phone.$touch()
            },

            async saveBusinessInfo() {
                logAnalyticsEvent(`company_save_info_click`)
                this.$v.organization.$touch()

                if (!this.$v.organization.$invalid) {
                    try {
                        this.saveInfo = true;
                        if (!this.organization.website.startsWith('https://') && !this.organization.website.startsWith('http://') && this.organization.website.length != 0) {
                            this.organization.website = 'https://' + this.organization.website
                            this.$v.organization.website.$touch()
                        }
                        const body = {
                            official_name: this.organization.official_name,
                            contact_email: this.organization.contact_email,
                            phone: this.organization.phone,
                            address: this.organization.address,
                            website: this.organization.website
                        }
                        const responce = await this.saveBusinessInfoAction(body);
                        if (responce.data.success) {
                            logAnalyticsEvent(`company_save_info_success`)
                            this.customModalSettings = {
                                message: this.translate('Success'),
                                icon: 'ic_allow.svg'
                            }
                        }
                        this.saveInfo = false;
                    } catch (e) {
                        logError('company_save_info_error', e.data.error.message || '')
                        this.showErrorModal(e.data.error.message)
                        this.saveInfo = false;
                    }
                } else {
                    logAnalyticsEvent(`company_save_info_click_invalid_input`)
                }

            },

            async setMultipleSitesModeConfirm() {
                logAnalyticsEvent(`company_multiple_sites_click`)
                this.customModalSettings = {
                    message: this.translate('goingToMultipleSitesMode'),
                    onOkHandler: this.setMultipleSitesMode,
                    showCancelButton: true,
                    okButtonText: this.translate('OK'),
                    cancelButtonText: this.translate('Cancel'),
                }
            },

            async setMultipleSitesMode() {
                this.canDoMultipleLoader = true
                try {
                    const response = await this.setMultipleSitesModeAction()
                    if (response.success) {
                        logAnalyticsEvent(`company_multiple_sites_success`)
                        this.canDoMultiple = false
                        await this.fetchUserInfo()
                    }
                } catch (e) {
                    logError('company_multiple_sites_error', e.data.error.message || '')
                    this.showErrorModal(e.data.error.message)
                } finally {
                    this.canDoMultipleLoader = false
                }
            },

            async onAIFilterClick() {
                try {
                    let body = {
                        enabled: this.aiAssistEnabled,
                    }
                    this.aiAssistLoader = true
                    const response = await this.setAIAssistMode(body)
                    if (response.success) {
                        logAnalyticsEvent(`company_ai_mode_changed`)
                    }
                } catch (e) {
                    logError('company_ai_mode_error', e.data.error.message || '')
                    this.showErrorModal(e.data.error.message)
                } finally {
                    this.aiAssistLoader = false
                }
            },

        }
    }

</script>

<style scoped>
    .company-tab {
        padding-bottom: 48px;
    }

    #company-tab .columns {
        display: flex;
        justify-content: space-between;
        margin-top: 23px;
    }

    #company-tab .columns > div {
        width: calc(50% - 12px);
        border-radius: 4px;
        background-color: #FFFFFF;
        box-shadow: 0 5px 11px 0 rgba(0, 0, 0, 0.11);
        padding: 24px;
        position: relative;
    }

    #company-tab .columns > div h4 {
        font-size: 20px;
        font-weight: bold;
        line-height: 56px;
    }


    .company-info {
        max-width: 624px;
    }

    .company-info > div.hidden {
        visibility: hidden;
    }

    .company-info .button-group {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
    }

    .company-info form .company-input {
        display: flex;
        align-items: center;
        margin-bottom: -2px;
    }

    .company-info form .company-input img {
        margin-right: 8px;
        opacity: .5;
    }

    /*company-managers*/
    .company-managers {
        max-width: 632px;
    }

    .company-managers p.description {
        color: #2F2F2F;
        font-size: 14px;
        line-height: 22px;
    }

    .company-managers .invite-form {
        display: flex;
        align-items: center;
    }

    .company-managers .invite-form .md-field {
        margin-right: 24px;
        width: calc(100% - 192px);
    }

    .company-managers .invite-form button {
        width: 168px;
        background-color: #2F2F2F;
        border-radius: 0;
    }

    .company-managers .invite-form button span {
        font-size: 14px;
        line-height: 34px;
        font-weight: 500;
    }

    .active-managers h5 {
        color: #2F2F2F;
        font-size: 16px;
        font-weight: bold;
        line-height: 26px;
        margin-top: 20px;
        margin-bottom: 0;
    }

    .active-managers .active-managers-list {
        max-height: 245px;
        min-height: 200px;
        overflow-y: auto;
        position: relative;
    }

    .active-managers ul {
        /*display: grid;*/
        display: table;
        width: 100%;
    }

    .active-managers ul li {
        display: table-row;
        /*display: grid;*/
        /*grid-template-columns: auto 80px  129px;*/
    }

    .active-managers ul li > * {
        display: table-cell;
        margin-bottom: 10px;
        margin-top: 10px;
        vertical-align: middle;
    }

    .active-managers ul li a {
        color: #1976D2;
        font-size: 14px;
        line-height: 22px;
        font-weight: 500;
    }

    .active-managers ul li .status {
        min-width: 88px;
        /*width: 100%;*/
        text-align: center;
    }

    .active-managers ul li .status > span {
        border-radius: 12px;
        font-size: 12px;
        line-height: 15px;
        text-align: center;
        color: #fff;
        padding: 4px 18px;
        width: 80%;
        display: block;
        margin: auto;
    }

    .active-managers ul li .status > span.accepted {
        background-color: #36BC00;
    }

    .active-managers ul li .status > span.pending {
        background-color: #9E9E9E;
    }

    .active-managers ul li button {
        border-radius: 0;
        background-color: transparent;
        border: 0;
        box-shadow: none;
        padding-right: 0;
        outline: none;
        width: 100%;
    }

    .active-managers ul li button > div {
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }

    .active-managers ul li button > div span {
        color: #2F2F2F;
        font-size: 14px;
        font-weight: bold;
        line-height: 18px;
        margin-left: 8px;
    }


    /*other*/
    .md-field.md-invalid label {
        color: red;
    }

    /*.md-field label,*/
    .md-field:not(.md-has-value) label {
        font-size: 14px;
    }

    .md-field .md-input {
        font-size: 14px;
        font-weight: 500;
        color: #2F2F2F;
    }

    .md-field.md-has-value label,
    .md-field.md-focused label {
        font-size: 13px;
    }

    .md-field.md-invalid:before,
    .md-field.md-invalid:after {
        background-color: rgba(255, 0, 0, 0.7);
    }

    .phone-custom.md-field label {
        left: 110px;
        z-index: 10;
    }

    .phone-custom.md-field.md-has-value label,
    .phone-custom.md-field.md-focused label {
        left: 0;
    }

    .cb-description {
        margin-left: 24px;
        margin-top: 4px;
        color: #2F2F2F;
        letter-spacing: 0;
        line-height: 22px;
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        padding-right: 15px;
    }

    @media screen and (max-width: 1000px) {

        #company-tab .columns:first-child {
            margin-top: 8px;
        }

        #company-tab .columns:not(:first-child) {
            margin-top: 0;
        }

        .company-info .button-group {
            flex-direction: column;
        }

        #company-tab .columns {
            flex-direction: column;
        }

        #company-tab .columns > div {
            width: 100%;
            max-width: 100%;
            margin-bottom: 25px;
        }

        .company-managers .invite-form {
            flex-direction: column;
        }

        .company-managers .invite-form .md-field {
            width: 100%;
            margin-right: 0;
        }

        .company-managers .invite-form button {
            width: 100%;
            margin-top: 15px;
        }

        .active-managers ul li {
            display: grid;
            grid-template-columns: auto 24px;
            grid-template-rows: repeat(2, 1fr);
            margin-bottom: 10px;
        }

        .active-managers ul li > * {
            margin-bottom: 0;
        }

        .active-managers ul li a {
            grid-column: 1 / span 2;
            grid-row: 1 / span 1;
        }

        .active-managers ul li .status {
            min-width: 100%;
            max-width: 100%;
            grid-column: 1 / span 1;
            grid-row: 2 / span 1;
        }

        .active-managers ul li .status > span {
            width: 90%;
            display: block;
            margin: 0;
        }

        .active-managers ul li button {
            grid-column: 2 / span 1;
            grid-row: 2 / span 1;
        }

        .active-managers ul li button span {
            display: none;
        }

    }

</style>
<style>
    .company-info .vue-tel-input {
        position: absolute;
        z-index: 2;
        border: none;
        width: 100%;
    }

    .company-info .vue-tel-input:focus {
        outline: none;
    }

    .company-info .vue-tel-input:focus-within {
        border: none;
        box-shadow: none;
    }

    .company-info .vti__input {
        background: none;
    }

    .company-info .vti__dropdown {
        top: -2px;
    }

    .vti__dropdown-item {
        width: 100%;
        margin: 0;
    }

    .company-managers .md-field .md-count,
    .company-managers .md-field .md-helper-text {
        color: #6D6D6D;
    }

</style>
